'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@achra/ui/lib/utils';
import ReactECharts, { type EChartsOption } from 'echarts-for-react';
import { useEffect, useMemo, useRef } from 'react';
import { replaceAllNumberLetOneBeforeDot } from '@/modules/finances/lib/expenses-metric-chart-utils';
import { formatterReservesWaterfallChart, getReservesWaterfallXAxisRichStyles, getWaterfallAxisLabels } from '@/modules/finances/lib/reserves-waterfall-chart-utils';
import { useMediaQuery } from '@/modules/shared/hooks/use-media-query';
import { LegendItem } from './legend-item';
interface ReservesWaterfallChartGraphProps {
  year: string;
  selectedGranularity: 'monthly' | 'quarterly' | 'annual';
  series: NonNullable<EChartsOption['series']>;
}
function ReservesWaterfallChartGraph(t0) {
  const $ = _c(61);
  const {
    year,
    selectedGranularity,
    series
  } = t0;
  const chartRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      to: "sm"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isLessMobile = useMediaQuery(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      from: "sm",
      to: "md"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isMobile = useMediaQuery(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      from: "md",
      to: "lg"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isTablet = useMediaQuery(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      from: "lg",
      to: "xl"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const isDesktop1024 = useMediaQuery(t4);
  const isMobileOrLess = isMobile || isLessMobile;
  const showLineYear = isMobileOrLess && (selectedGranularity === "monthly" || selectedGranularity === "quarterly");
  let t5;
  if ($[4] !== isMobileOrLess || $[5] !== selectedGranularity) {
    t5 = getWaterfallAxisLabels(selectedGranularity, isMobileOrLess);
    $[4] = isMobileOrLess;
    $[5] = selectedGranularity;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const axisLabels = t5;
  let t6;
  if ($[7] !== isMobile) {
    t6 = getReservesWaterfallXAxisRichStyles(isMobile);
    $[7] = isMobile;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const xAxisRichStyles = t6;
  let t7;
  bb0: {
    let maxValue = 0;
    for (const serie of series) {
      if (!serie || typeof serie !== "object" || !("data" in serie)) {
        continue;
      }
      const serieData = serie.data;
      if (!Array.isArray(serieData)) {
        continue;
      }
      for (const point of serieData) {
        if (typeof point === "number") {
          if (point > maxValue) {
            maxValue = point;
          }
          continue;
        }
        if (point && typeof point === "object" && "value" in point && typeof point.value === "number") {
          if (point.value > maxValue) {
            maxValue = point.value;
          }
        }
      }
    }
    if (maxValue === 0) {
      t7 = undefined;
      break bb0;
    }
    t7 = Math.ceil(maxValue * 1.14);
  }
  const yAxisMax = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      show: false
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = isLessMobile || isMobile ? 170 : isTablet ? 230 : isDesktop1024 ? 225 : 312;
  const t10 = isLessMobile || isMobile || isTablet ? 10 : isDesktop1024 ? 30 : 11;
  const t11 = isLessMobile ? 28 : isMobile ? 36 : isTablet ? 48 : isDesktop1024 ? 56 : 64;
  const t12 = isMobileOrLess ? 24 : isTablet ? 30 : 40;
  let t13;
  if ($[10] !== t10 || $[11] !== t11 || $[12] !== t12 || $[13] !== t9) {
    t13 = {
      containLabel: true,
      height: t9,
      top: t10,
      right: t11,
      left: t12
    };
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t9;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  let t15;
  let t16;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      show: false
    };
    t15 = {
      show: false
    };
    t16 = {
      show: false
    };
    $[15] = t14;
    $[16] = t15;
    $[17] = t16;
  } else {
    t14 = $[15];
    t15 = $[16];
    t16 = $[17];
  }
  const t17 = isMobile ? 12 : 16;
  const t18 = isMobile ? 12 : 14;
  let t19;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [0, 5, 0, 5];
    $[18] = t19;
  } else {
    t19 = $[18];
  }
  let t20;
  if ($[19] !== axisLabels || $[20] !== isMobileOrLess || $[21] !== year) {
    t20 = (value, index) => formatterReservesWaterfallChart({
      axisLabels,
      isMobileOrLess,
      year,
      value,
      index
    });
    $[19] = axisLabels;
    $[20] = isMobileOrLess;
    $[21] = year;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] !== t17 || $[24] !== t18 || $[25] !== t20 || $[26] !== xAxisRichStyles) {
    t21 = {
      margin: t17,
      color: "color-mix(in srgb, var(--color-foreground) 70%, transparent)",
      align: "center",
      fontFamily: "var(--font-open-sans-condensed)",
      fontWeight: 700,
      fontSize: t18,
      interval: 0,
      padding: t19,
      formatter: t20,
      rich: xAxisRichStyles
    };
    $[23] = t17;
    $[24] = t18;
    $[25] = t20;
    $[26] = xAxisRichStyles;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== axisLabels || $[29] !== t21) {
    t22 = {
      type: "category",
      data: axisLabels,
      splitLine: t14,
      axisLine: t15,
      axisTick: t16,
      axisLabel: t21
    };
    $[28] = axisLabels;
    $[29] = t21;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  const t23 = isMobileOrLess ? 6 : 12;
  let t24;
  if ($[31] !== isMobileOrLess || $[32] !== t23) {
    t24 = {
      show: true,
      fontFamily: "var(--font-open-sans-condensed)",
      margin: t23,
      formatter(value_0, index_0) {
        if (value_0 === 0 && index_0 === 0) {
          return value_0.toString();
        }
        return replaceAllNumberLetOneBeforeDot(value_0, true);
      },
      color: "var(--color-foreground)",
      fontSize: isMobileOrLess ? 12 : 14,
      fontWeight: 700
    };
    $[31] = isMobileOrLess;
    $[32] = t23;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  let t26;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      show: false
    };
    t26 = {
      lineStyle: {
        color: "var(--color-border)",
        width: 1
      }
    };
    $[34] = t25;
    $[35] = t26;
  } else {
    t25 = $[34];
    t26 = $[35];
  }
  let t27;
  if ($[36] !== t24 || $[37] !== yAxisMax) {
    t27 = {
      min: 0,
      max: yAxisMax,
      show: true,
      axisLabel: t24,
      type: "value",
      splitNumber: 9,
      axisLine: t25,
      splitLine: t26
    };
    $[36] = t24;
    $[37] = yAxisMax;
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] !== series || $[40] !== t13 || $[41] !== t22 || $[42] !== t27) {
    t28 = {
      tooltip: t8,
      grid: t13,
      xAxis: t22,
      yAxis: t27,
      series
    };
    $[39] = series;
    $[40] = t13;
    $[41] = t22;
    $[42] = t27;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  const options = t28;
  let t29;
  let t30;
  if ($[44] !== options) {
    t29 = () => {
      chartRef.current?.getEchartsInstance().setOption(options, {
        notMerge: true
      });
    };
    t30 = [options];
    $[44] = options;
    $[45] = t29;
    $[46] = t30;
  } else {
    t29 = $[45];
    t30 = $[46];
  }
  useEffect(t29, t30);
  let t31;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = cn("relative flex w-full flex-row justify-center", "h-57 md:h-67 lg:h-72 xl:h-89 2xl:h-89");
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  let t32;
  let t33;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      height: "100%",
      width: "100%"
    };
    t33 = {
      renderer: "svg"
    };
    $[48] = t32;
    $[49] = t33;
  } else {
    t32 = $[48];
    t33 = $[49];
  }
  let t34;
  if ($[50] !== options) {
    t34 = <ReactECharts ref={chartRef} option={options} style={t32} opts={t33} />;
    $[50] = options;
    $[51] = t34;
  } else {
    t34 = $[51];
  }
  let t35;
  if ($[52] !== showLineYear || $[53] !== year) {
    t35 = showLineYear && <div className="border-border absolute right-1.5 bottom-0 left-8 h-3 rounded-b-sm border-r border-b border-l"><div className="bg-background text-border absolute -bottom-1.5 left-1/2 w-13 -translate-x-1/2 text-center text-xs font-(--font-open-sans-condensed) tracking-wide">{year}</div></div>;
    $[52] = showLineYear;
    $[53] = year;
    $[54] = t35;
  } else {
    t35 = $[54];
  }
  let t36;
  if ($[55] !== t34 || $[56] !== t35) {
    t36 = <div className={t31}>{t34}{t35}</div>;
    $[55] = t34;
    $[56] = t35;
    $[57] = t36;
  } else {
    t36 = $[57];
  }
  let t37;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div className="mt-2 flex flex-wrap items-center justify-center gap-4 md:gap-8"><LegendItem color="#329DFF" label="Reserves Balance" /><LegendItem color="#EA4335" label="Outflow" /><LegendItem color="#34A853" label="Inflow" /></div>;
    $[58] = t37;
  } else {
    t37 = $[58];
  }
  let t38;
  if ($[59] !== t36) {
    t38 = <div className="flex w-full flex-col items-center">{t36}{t37}</div>;
    $[59] = t36;
    $[60] = t38;
  } else {
    t38 = $[60];
  }
  return t38;
}
export { ReservesWaterfallChartGraph };